import React, { useMemo, useRef, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import BaseTable from 'react-base-table';
import classNames from 'classnames';
import { sortableContainer, sortableHandle } from 'react-sortable-hoc';
import {
  ContextMenu,
  hideMenu,
  MenuItem,
  showMenu,
  SubMenu,
} from 'react-contextmenu';
import { __ } from '../../../core/utils';
import GrabberIcon from '../../../assets/images/grabber-16x16.svg';
import MenuHIcon from '../../../assets/images/menu-h-15x15.svg';
import ArrowRightSmallIcon from '../../../assets/images/arrow-right-small-15x15.svg';
import PlanningTemplateRow from './PlanningTemplateRow';
import styles from '../../../styles/views/company/PlanningTemplates.scss';

const DraggableContainer = sortableContainer(({ children }) => children);
const DragHandle = sortableHandle(() => (
  <div className={styles.templateGrabber}>
    <GrabberIcon />
  </div>
));

const TYPE_LABELS = {
  stage: 'Stage',
  substage: 'Substage',
  group: 'Group',
};

const TYPE_LEVELS = {
  stage: 'L1',
  substage: 'L2',
  group: 'L3',
};

const PlanningTemplateTable = ({
  depth,
  nodes,
  onAdd,
  onDuplicate,
  onRemove,
  onReorder,
  readOnly,
}) => {
  const tableRef = useRef();
  const [focusedRow, setFocusedRow] = useState();
  const [isDragging, setIsDragging] = useState(false);
  const visibleNodes = _.filter(nodes, (node) => node.depth < depth);

  const openContextMenu = (event, rowData) => {
    if (readOnly) {
      return;
    }

    const position = { x: event.clientX, y: event.clientY };

    hideMenu();
    setFocusedRow(rowData);

    window.requestAnimationFrame(() => {
      showMenu({
        position,
        id: 'planning_template_context_menu',
      });
    });
  };

  const columns = useMemo(
    () => [
      {
        key: 'order',
        title: '',
        width: 44,
        flexShrink: 0,
        cellRenderer: () => (readOnly ? null : <DragHandle />),
      },
      {
        key: 'name',
        dataKey: 'name',
        title: __('planning-templates.builder.structure'),
        width: 500,
        flexGrow: 1,
        cellRenderer: ({ cellData, rowData }) => (
          <div
            className={styles.templateNodeName}
            style={{ paddingLeft: rowData.depth * 36 }}
          >
            {cellData}
          </div>
        ),
      },
      {
        key: 'type',
        dataKey: 'type',
        title: __('planning-templates.builder.level'),
        width: 160,
        flexShrink: 0,
        cellRenderer: ({ cellData }) => (
          <span className={styles.templateNodeType}>
            {__(
              `planning-templates.type.${cellData}`,
              {},
              TYPE_LABELS[cellData]
            )}
          </span>
        ),
      },
      {
        key: 'actions',
        title: '',
        width: 56,
        flexShrink: 0,
        align: 'center',
        cellRenderer: ({ rowData }) =>
          readOnly ? null : (
            <button
              type='button'
              className={styles.templateNodeActions}
              onClick={(event) => openContextMenu(event, rowData)}
            >
              <MenuHIcon />
            </button>
          ),
      },
    ],
    [readOnly]
  );

  const getContainer = () =>
    tableRef.current
      .getDOMNode()
      .querySelector('.BaseTable__table-main .BaseTable__body');

  const getHelperContainer = () =>
    tableRef.current.getDOMNode().querySelector('.BaseTable__table-main');

  return (
    <div className={styles.templateTable}>
      <AutoSizer>
        {({ height, width }) => (
          <DraggableContainer
            useDragHandle
            getContainer={getContainer}
            helperContainer={getHelperContainer}
            lockAxis='y'
            onSortStart={() => setIsDragging(true)}
            onSortEnd={({ collection, oldIndex, newIndex }) => {
              setIsDragging(false);

              if (!readOnly) {
                onReorder(collection, oldIndex, newIndex);
              }
            }}
          >
            <BaseTable
              ref={tableRef}
              className={classNames(
                styles.templateBaseTable,
                isDragging && styles.isDragging
              )}
              columns={columns}
              data={visibleNodes}
              width={width}
              height={height}
              rowKey='client_id'
              headerHeight={48}
              rowHeight={48}
              headerClassName={styles.templateTableHeader}
              rowClassName={({ rowData }) =>
                classNames(
                  styles.templateTableRow,
                  rowData.client_id === focusedRow?.client_id &&
                    styles.focusedRow
                )
              }
              rowProps={({ rowData }) => ({
                tagName: PlanningTemplateRow,
                index: rowData.position,
                collection: rowData.parent_client_id || 'root',
                planningTemplateRow: rowData,
                setFocusedRow,
                readOnly,
                onClick: () => setFocusedRow(rowData),
              })}
              ignoreFunctionInColumnCompare={false}
              emptyRenderer={
                <div className={styles.emptyState}>
                  {__('planning-templates.builder.empty')}
                </div>
              }
            />
          </DraggableContainer>
        )}
      </AutoSizer>

      <ContextMenu
        id='planning_template_context_menu'
        className={styles.contextMenu}
        onHide={() => setFocusedRow(undefined)}
      >
        {focusedRow && (
          <>
            <MenuItem disabled>
              <div
                className={classNames(
                  styles.contextMenuItem,
                  styles.disabledContextMenuItem
                )}
              >
                <span>{__('planning-templates.builder.move')}</span>
              </div>
            </MenuItem>

            <MenuItem onClick={() => onDuplicate(focusedRow)}>
              <div className={styles.contextMenuItem}>
                <span>{__('planning-templates.builder.duplicate')}</span>
              </div>
            </MenuItem>

            <MenuItem divider className={styles.contextMenuDivider} />

            <SubMenu
              rtl
              title={
                <div
                  className={classNames(
                    styles.contextMenuItem,
                    styles.leftSubmenuItem
                  )}
                >
                  <span>{__('planning-templates.builder.insert')}</span>
                  <ArrowRightSmallIcon />
                </div>
              }
            >
              <MenuItem
                onClick={(event) =>
                  onAdd(
                    focusedRow.type,
                    {
                      parentClientId: focusedRow.parent_client_id,
                      afterClientId: focusedRow.client_id,
                    },
                    event
                  )
                }
              >
                <div
                  className={classNames(
                    styles.contextMenuItem,
                    styles.submenuItem
                  )}
                >
                  <span>
                    {__(
                      `planning-templates.type.${focusedRow.type}`,
                      {},
                      TYPE_LABELS[focusedRow.type]
                    )}
                  </span>
                  <span className={styles.contextMenuLevel}>
                    {TYPE_LEVELS[focusedRow.type]}
                  </span>
                </div>
              </MenuItem>

              {focusedRow.type === 'stage' && (
                <MenuItem
                  onClick={(event) =>
                    onAdd(
                      'substage',
                      { parentClientId: focusedRow.client_id },
                      event
                    )
                  }
                >
                  <div
                    className={classNames(
                      styles.contextMenuItem,
                      styles.submenuItem
                    )}
                  >
                    <span>{__('planning-templates.type.substage')}</span>
                    <span className={styles.contextMenuLevel}>L2</span>
                  </div>
                </MenuItem>
              )}

              {focusedRow.type === 'substage' && (
                <MenuItem
                  onClick={(event) =>
                    onAdd(
                      'group',
                      { parentClientId: focusedRow.client_id },
                      event
                    )
                  }
                >
                  <div
                    className={classNames(
                      styles.contextMenuItem,
                      styles.submenuItem
                    )}
                  >
                    <span>{__('planning-templates.type.group')}</span>
                    <span className={styles.contextMenuLevel}>L3</span>
                  </div>
                </MenuItem>
              )}
            </SubMenu>

            <MenuItem divider className={styles.contextMenuDivider} />

            <MenuItem onClick={() => onRemove(focusedRow)}>
              <div
                className={classNames(
                  styles.contextMenuItem,
                  styles.removeContextMenuItem
                )}
              >
                <span>{__('planning-templates.builder.remove')}</span>
              </div>
            </MenuItem>
          </>
        )}
      </ContextMenu>
    </div>
  );
};

export default PlanningTemplateTable;
